import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import * as Parse from 'parse';
import * as Sentry from '@sentry/react';

import {AppThunk} from 'state';

export type Call = {
  id: string;
  tableNumber: string;
  type: 'waiterCall' | 'waiterPayment' | 'roomService';
  Items: string[];
};

interface State {
  error: string | null;
  loaded: boolean;
  queue: Call[];
  ItemsArray: Array<{items: string[]; id: string; tableNumber: string}>;
  ItemsString: Array<{itemString: string; tableNumber: string}>;
}

const initialState: State = {
  error: null,
  loaded: false,
  queue: [],
  ItemsArray: [],
  ItemsString: [],
};

const waiterCallSlice = createSlice({
  name: 'waiterCall',
  initialState,
  reducers: {
    fetchQueueStart(state) {
      state.error = null;
    },
    fetchQueueSuccess(state, action: PayloadAction<Call[]>) {
      state.queue = action.payload;
      state.ItemsArray = state.queue
        .filter((item) => item.type === 'waiterCall')
        .map((item) => ({
          items: item.Items,
          id: item.id,
          tableNumber: item.tableNumber,
        }));
      state.ItemsString = updateItemsString(state.queue);
      state.loaded = true;
    },
    appendToQueue(state, action: PayloadAction<Call>) {
      state.queue.push(action.payload);
      if (action.payload.type === 'waiterCall') {
        state.ItemsArray.push({
          items: action.payload.Items,
          id: action.payload.id,
          tableNumber: action.payload.tableNumber,
        });
        state.ItemsString = updateItemsString(state.queue);
      }
    },
    removeFromQueue(
      state,
      action: PayloadAction<{items: string[]; tableNumber: string}>,
    ) {
      // Find the table entry in state.ItemsString
      const tableEntryIndex = state.ItemsString.findIndex(
        (entry) => entry.tableNumber === action.payload.tableNumber,
      );
      // If the table entry exists, proceed with item removal
      if (tableEntryIndex !== -1) {
        const tableEntry = state.ItemsString[tableEntryIndex];
        action.payload.items.forEach((item) => {
          const itemsArray = Array.from(tableEntry.itemString);
          const itemIndex = itemsArray.findIndex((str) => str.includes(item));
          if (itemIndex !== -1) {
            itemsArray.splice(itemIndex, 1);
          }
        });
        // Update state.ItemsString with the modified table entry
        state.ItemsString[tableEntryIndex] = tableEntry;
      }
      // Optional: Remove empty table entries if necessary
      state.ItemsString = state.ItemsString.filter(
        (entry) => entry.itemString.length > 0,
      );
    },
    fetchQueueFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    resetQueue(state) {
      state.loaded = false;
      state.error = null;
      state.queue = [];
      state.ItemsString = updateItemsString(state.queue);
    },
    resolveCallSuccess(state, action: PayloadAction<string>) {
      const id = action.payload;
      const index = state.queue.findIndex((item) => item.id === id);
      if (index !== -1) state.queue.splice(index, 1);
      state.error = null;
    },
    resolveCallFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    deleteItemsArray(state, action: PayloadAction<string>) {
      const idToDelete = action.payload;
      state.ItemsArray = state.ItemsArray.filter(
        (item) => item.id !== idToDelete,
      );
      state.ItemsString = updateItemsString(state.queue);
    },
    purge() {
      return initialState;
    },
  },
});

export const {
  fetchQueueStart,
  fetchQueueSuccess,
  fetchQueueFailed,
  appendToQueue,
  removeFromQueue,
  resetQueue,
  resolveCallSuccess,
  resolveCallFailed,
  deleteItemsArray,
  purge,
} = waiterCallSlice.actions;

export default waiterCallSlice.reducer;

///

export const updateItemsString = (queue) => {
  const updatedItemsString = queue
    .filter((entity) => entity.type === 'waiterCall')
    .reduce((acc, entity) => {
      const tableNumber = entity.tableNumber;
      const items = entity.Items;
      let tableEntry = acc.find((entry) => entry.tableNumber === tableNumber);
      if (!tableEntry) {
        tableEntry = {
          itemString: [],
          tableNumber,
        };
        acc.push(tableEntry);
      }
      items.forEach((item) => {
        const itemCount = items.filter((i) => i === item).length;
        const itemString = `${itemCount > 1 ? `${itemCount} ${item}` : item}`;
        tableEntry.itemString.push(itemString);
      });
      return acc;
    }, []);
  return updatedItemsString;
};

///
/**
 * Thunks
 */

export const fetchQueue = (): AppThunk => async (dispatch) => {
  try {
    dispatch(fetchQueueStart());

    const currentUser = Parse.User.current()!;
    const restaurant = currentUser.get('restaurant');

    const query = new Parse.Query('WaiterCall');
    query.equalTo('restaurant', restaurant);

    const waiterCalls = await query.find();

    const queue = waiterCalls.map((waiterCall) => {
      return {
        id: waiterCall.id,
        tableNumber: waiterCall.get('tableNumber'),
        type: waiterCall.get('type'),
        Items: waiterCall.get('items'),
      };
    });

    dispatch(fetchQueueSuccess(queue));
  } catch (error) {
    dispatch(fetchQueueFailed(error.toString()));
    Sentry.captureException(error);
    // eslint-disable-next-line no-console
    console.error('fetchQueue error', error);
  }
};

export const resolveCall =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      const query = new Parse.Query('WaiterCall');

      const waiterCall = await query.get(id);

      await waiterCall.destroy();

      dispatch(resolveCallSuccess(id));
    } catch (error) {
      dispatch(resolveCallFailed(error.toString()));
      Sentry.captureException(error);
      // eslint-disable-next-line no-console
      console.error('resolveCall error', error);
    }
  };
